import { useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { doc, onSnapshot } from 'firebase/firestore';
import { functions, db } from '../../lib/firebase.js';
import { useAuth } from '../../lib/auth.js';
import { DropboxSyncPanel } from './DropboxSyncPanel.js';
import { DropboxCrawlPanel } from './DropboxCrawlPanel.js';

interface DbxEntry {
  '.tag': 'file' | 'folder';
  id: string;
  name: string;
  path_display: string;
  size?: number;
  server_modified?: string;
}

export function DropboxIntegration() {
  const { user, role } = useAuth();
  const [connected, setConnected] = useState<boolean | null>(null);
  const [accountId, setAccountId] = useState<string | null>(null);
  const [path, setPath] = useState('');
  const [entries, setEntries] = useState<DbxEntry[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [crawlMsg, setCrawlMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    return onSnapshot(doc(db, 'integrations', user.uid), (snap) => {
      const dbx = snap.data()?.dropbox;
      setConnected(!!dbx?.refreshToken);
      setAccountId(dbx?.accountId ?? null);
    });
  }, [user]);

  async function connect() {
    setBusy(true);
    setErr(null);
    try {
      const fn = httpsCallable<unknown, { url: string }>(functions, 'dropboxOAuthStart');
      const r = await fn({});
      window.location.href = r.data.url;
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to start OAuth');
    } finally {
      setBusy(false);
    }
  }

  async function disconnect() {
    if (!confirm('Disconnect Dropbox? Crawls will stop until you reconnect.')) return;
    setBusy(true);
    try {
      const fn = httpsCallable(functions, 'dropboxDisconnect');
      await fn({});
    } finally {
      setBusy(false);
    }
  }

  async function browse(toPath: string) {
    setBusy(true);
    setErr(null);
    try {
      const fn = httpsCallable<unknown, { entries: DbxEntry[] }>(functions, 'dropboxList');
      const r = await fn({ path: toPath });
      setPath(toPath);
      setEntries(r.data.entries);
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'List failed');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-4xl px-6 py-8 space-y-6">
      <header>
        <div className="text-[10px] uppercase tracking-wider text-stone-400">Integration</div>
        <h1 className="text-2xl font-semibold text-stone-800">Dropbox</h1>
        <p className="mt-1 text-sm text-stone-600">
          Connect your Dropbox so SFP can browse folders, crawl files into the document pipeline, or push
          generated outputs back out.
        </p>
      </header>

      <div className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
        {connected === null ? (
          <div className="text-sm text-stone-500">Checking connection…</div>
        ) : connected ? (
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-emerald-700">Connected</div>
              <div className="font-mono text-xs text-stone-600">{accountId ?? '—'}</div>
            </div>
            <button
              type="button"
              onClick={disconnect}
              disabled={busy}
              className="rounded border border-[#9C4A3A] px-3 py-1.5 text-xs text-[#9C4A3A] hover:bg-[#9C4A3A]/10"
            >
              Disconnect
            </button>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-4">
            <div>
              <div className="text-sm font-semibold text-stone-800">Not connected</div>
              <div className="text-xs text-stone-500">
                Click connect — Dropbox will ask you to authorize SFP, then redirect you back here.
              </div>
            </div>
            <button
              type="button"
              onClick={connect}
              disabled={busy}
              className="rounded bg-[color:var(--sfp-primary)] px-4 py-2 text-sm font-medium text-white"
            >
              {busy ? 'Starting…' : 'Connect Dropbox'}
            </button>
          </div>
        )}
        {err && <div className="mt-3 text-xs text-[#9C4A3A]">{err}</div>}
      </div>

      {connected && (
        <>
          <div className="rounded-2xl border border-stone-200 bg-white p-5 shadow-sm">
            <h2 className="text-sm font-semibold text-stone-800">Browse</h2>
            <div className="mt-2 flex gap-2">
              <input
                value={path}
                onChange={(e) => setPath(e.target.value)}
                placeholder='Path (e.g. "/SFP/Documents" or empty for root)'
                className="flex-1 rounded border border-stone-300 px-2 py-1.5 text-xs"
              />
              <button
                type="button"
                onClick={() => void browse(path)}
                disabled={busy}
                className="rounded bg-stone-900 px-3 py-1.5 text-xs text-white"
              >
                List
              </button>
            </div>
            {entries && (
              <div className="mt-3 max-h-96 overflow-y-auto rounded border border-stone-200">
                {entries.length === 0 ? (
                  <div className="px-3 py-4 text-center text-xs text-stone-400">Empty.</div>
                ) : (
                  <table className="w-full text-xs">
                    <tbody>
                      {entries.map((e) => (
                        <tr key={e.id} className="border-b border-stone-100">
                          <td className="px-3 py-1.5 text-stone-400 w-6">
                            {e['.tag'] === 'folder' ? '▸' : '▤'}
                          </td>
                          <td className="px-2 py-1.5">
                            {e['.tag'] === 'folder' ? (
                              <button
                                type="button"
                                onClick={() => void browse(e.path_display)}
                                className="text-[color:var(--sfp-primary)] hover:underline"
                              >
                                {e.name}
                              </button>
                            ) : (
                              <span className="text-stone-700">{e.name}</span>
                            )}
                          </td>
                          <td className="px-3 py-1.5 text-right text-stone-400">
                            {e.size != null ? bytesHuman(e.size) : ''}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>

          {role === 'admin' && <DropboxCrawlPanel initialPath={path} />}

          {role === 'admin' && <DropboxSyncPanel />}
        </>
      )}
    </div>
  );
}

function bytesHuman(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / 1024 / 1024).toFixed(1)} MB`;
  return `${(n / 1024 / 1024 / 1024).toFixed(2)} GB`;
}
